import { usePathname } from "next/navigation";
import { useState, useEffect, useCallback, useRef } from "react";

type ImmersiveNavState = {
  isVisible: boolean;
  isImmersive: boolean;
};

type UseImmersiveNavProps = {
  isDrawerOpen?: boolean;
  isSearchDropdownOpen?: boolean;
  isContentNavTransparent?: boolean;
};

export const useImmersiveNav = ({
  isDrawerOpen = false,
  isSearchDropdownOpen = false,
  isContentNavTransparent = false,
}: UseImmersiveNavProps = {}) => {
  const pathname = usePathname();
  const path = pathname?.split("?")[0];

  const isHomepage = (() => {
    if (!path) return false;
    if (path === "/" || path === "/gb" || path === "/gb/") return true;
    if (path === "/topshop" || path === "/topman") return true;
    const segs = path.split("/").filter(Boolean);
    return (
      segs.length === 2 && ["topshop", "topman"].includes(segs[1] as string)
    );
  })();

  const isSticky = (() => {
    if (!path) return false;
    if (/^\/gb\/search(\/|$)/.test(path)) return true;
    if (/^\/gb\/(topshop|topman)\/category(\/|$)/.test(path)) return true;
    return false;
  })();

  const [state, setState] = useState<ImmersiveNavState>({
    isVisible: true,
    isImmersive: (isHomepage && !isSticky) || isContentNavTransparent,
  });

  const lastScrollY = useRef(0);

  // Reset nav state on route change
  useEffect(() => {
    if (isSticky) {
      setState({ isVisible: true, isImmersive: false });
      return;
    }
    setState({
      isVisible: true,
      isImmersive:
        (isHomepage || isContentNavTransparent) && window.scrollY <= 0,
    });
    lastScrollY.current = window.scrollY;
  }, [isSticky, isHomepage, isContentNavTransparent, path]);

  const handleMouseEnter = useCallback(() => {
    if (isSticky) return;
    setState((s) => ({ ...s, isImmersive: false }));
  }, [isSticky]);

  const handleMouseLeave = useCallback(() => {
    if (isSticky) return;
    const y = window.scrollY;
    if (
      isDrawerOpen ||
      isSearchDropdownOpen ||
      (y <= 0 && !isHomepage && !isContentNavTransparent)
    )
      return;
    setState((s) => ({ ...s, isImmersive: true }));
  }, [
    isSticky,
    isDrawerOpen,
    isSearchDropdownOpen,
    isHomepage,
    isContentNavTransparent,
  ]);

  useEffect(() => {
    if (isSticky) return; // no scroll-driven visibility on sticky pages

    const onScroll = () => {
      const current = window.scrollY;
      const scrollingDown = current > lastScrollY.current;
      const scrollingUp = current < lastScrollY.current;
      const atTop = current <= 0;

      setState((prev) => {
        if (isDrawerOpen || isSearchDropdownOpen) {
          return { ...prev, isVisible: true, isImmersive: false };
        }
        if (atTop) {
          return {
            isVisible: true,
            isImmersive: isHomepage || isContentNavTransparent,
          };
        }
        if (scrollingDown) {
          if (!prev.isVisible) return prev;
          return { ...prev, isVisible: false, isImmersive: false };
        }
        if (scrollingUp) {
          if (prev.isVisible && prev.isImmersive) return prev;
          return { ...prev, isVisible: true, isImmersive: true };
        }
        return prev;
      });

      lastScrollY.current = current;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [
    isSticky,
    isDrawerOpen,
    isSearchDropdownOpen,
    isHomepage,
    isContentNavTransparent,
  ]);

  // Force non-immersive while overlays open
  useEffect(() => {
    if (isDrawerOpen || isSearchDropdownOpen) {
      setState((s) => ({ ...s, isImmersive: false, isVisible: true }));
    }
  }, [isDrawerOpen, isSearchDropdownOpen]);

  return {
    isVisible: isSticky ? true : state.isVisible,
    isImmersive: isSticky ? false : state.isImmersive,
    isHomepage,
    isSticky,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };
};
